import React, { FC, useEffect, useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AlertDialog from 'components/UI/AlertDialog';
import { PrimaryBtn } from 'components/UI/Buttons';
import PreviewTemplate from 'components/common/previewTemplate';
import useCurrentUser from 'hooks/useCurrentUser';
import CreateTemplateForm from 'pages/Client/CreateTemplateForm';
import FollowUpConfigFormAttributes from 'pages/FollowUpConfig/FollowUpConfigFormAttributes';
import { useGetBusinessUnitTemplatesMutation } from 'services/api/businessUnits.api';
import { useCreateFollowUpMutation } from 'services/api/followUpConfig.api';
import { clientAttributes } from 'services/types/client';
import { FollowUpConfig, FollowUpConfigDraft } from 'services/types/followUpConfig';
import { TemplateResponse } from 'services/types/message';

const getTemplateBodyParameters = (template: TemplateResponse[number] | null) => {
  if (!template) return null;
  const bodyComponent = template.components.find((component) => component.type === 'BODY');
  if (bodyComponent && bodyComponent.example && bodyComponent.example.body_text) {
    return JSON.stringify(
      {
        bodyComponents: bodyComponent.example.body_text[0],
      },
      null,
      2
    );
  }
  return null;
};

interface FollowUpConfigFormProps {
  businessUnitId: number;
  selectFollowUp: (followUp: FollowUpConfig) => void;
}

const FollowUpConfigForm: FC<FollowUpConfigFormProps> = ({ businessUnitId, selectFollowUp }) => {
  const currentUser = useCurrentUser();
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateResponse[number] | null>(null);
  const [outboundCampaignId, setOutboundCampaignId] = useState<string | null>(null);
  const [openNewTemplate, setOpenNewTemplate] = useState<boolean>(false);
  const [templateBodyParameters, setTemplateBodyParameters] = useState<{ title: string; value: string }[] | null>(null);

  const [newFollowUpConfig, setNewFollowUpConfig] = useState<FollowUpConfigDraft>({
    businessUnitId,
    templateName: selectedTemplate?.name || null,
    minMinutesWaitTime: 10,
    maxMinutesWaitTime: 129_600, // 3 months
    minWorkingHour: 9,
    maxWorkingHour: 18,
    sendOnWeekend: false,
    identifier: outboundCampaignId || '',
    maxConsecutive: 1,
    maxAmountInConversation: 1,
    previousMessageKeepAliveIdentifier: null,
    requiredPreviousFollowUpsIds: null,
    conditionsToSend: JSON.stringify(
      {
        equal: [{ field: 'outboundCampaignId', value: outboundCampaignId }],
      },
      null,
      2
    ),
    templateComponents: getTemplateBodyParameters(selectedTemplate),
    isActive: true,
    clientsBatchSize: 50,
    functionName: null,
    prompt: null,
    specificTexts: null,
  });

  const [isError, setIsError] = useState(false);

  const [getBusinessUnitTemplates, { data: templates }] = useGetBusinessUnitTemplatesMutation();
  const [
    createFollowUpConfig,
    { isError: isCreateError, error: createError, isSuccess: isCreateSuccess, data: newFollowUp },
  ] = useCreateFollowUpMutation();

  useEffect(() => {
    if (businessUnitId) {
      getBusinessUnitTemplates(businessUnitId).catch((err) => console.error(err)); // eslint-disable-line no-console
    }
  }, [businessUnitId]);

  useEffect(() => {
    if (isCreateError) {
      setIsError(true);
    }
  }, [isCreateError]);

  useEffect(() => {
    if (isCreateSuccess && newFollowUp) {
      selectFollowUp(newFollowUp);
    }
  }, [isCreateSuccess]);

  useEffect(() => {
    setNewFollowUpConfig({
      ...newFollowUpConfig,
      templateName: selectedTemplate?.name || null,
      templateComponents: getTemplateBodyParameters(selectedTemplate),
    });
    setTemplateBodyParameters(null);
  }, [selectedTemplate]);

  useEffect(() => {
    if (templateBodyParameters) {
      setNewFollowUpConfig({
        ...newFollowUpConfig,
        templateComponents: JSON.stringify({
          bodyComponents: templateBodyParameters ? templateBodyParameters.map((p) => p.value) : null,
        }),
      });
    }
  }, [templateBodyParameters]);

  useEffect(() => {
    setNewFollowUpConfig({
      ...newFollowUpConfig,
      identifier: outboundCampaignId || '',
      conditionsToSend: JSON.stringify(
        {
          equal: [{ field: 'outboundCampaignId', value: outboundCampaignId }],
        },
        null,
        2
      ),
    });
  }, [outboundCampaignId]);

  const bodyComponentsLength = selectedTemplate?.components.find((c) => c.type === 'BODY' && c.example?.body_text)
    ?.example?.body_text?.[0].length;
  const handleTemplateBodyParametersChange = (parameters: { title: string; value: string }[]) => {
    if (bodyComponentsLength && parameters.length <= bodyComponentsLength) {
      setTemplateBodyParameters(parameters);
    }
  };

  const isCreateFollowUpDisabled =
    !newFollowUpConfig ||
    !selectedTemplate ||
    !outboundCampaignId ||
    (bodyComponentsLength ? bodyComponentsLength !== templateBodyParameters?.length : false);

  const getDisabledMessage = () => {
    if (!selectedTemplate) return 'No hay ninguna plantilla seleccionada';
    if (!outboundCampaignId) return 'No hay ningún identificador para la campaña';
    if (bodyComponentsLength && bodyComponentsLength !== templateBodyParameters?.length)
      return 'Falta seleccionar atributos para la plantilla';
    return null;
  };

  const handleCreateFollowUp = () => {
    if (isCreateFollowUpDisabled) return;
    createFollowUpConfig({
      ...newFollowUpConfig,
      conditionsToSend: JSON.parse(newFollowUpConfig.conditionsToSend) as string,
      templateComponents: newFollowUpConfig.templateComponents
        ? (JSON.parse(newFollowUpConfig.templateComponents) as string)
        : null,
    }).catch((err) => console.error(err)); // eslint-disable-line no-console
  };

  return (
    <Box width="50%">
      <AlertDialog
        openDialog={isError}
        setOpenDialog={setIsError}
        header={`Error al ${createError ? 'crear' : 'editar'} follow up`}
        msg={JSON.stringify(createError)}
        cancelMsg="Cerrar"
      />
      <Typography variant="h6">3. Crear campaña</Typography>
      <Typography variant="body1" pb={2}>
        La campaña se enviará a los clientes creados hasta hace 3 meses, que no se le haya enviado esta campaña
        anteriormente. La campaña se enviará entre 9 y 18 horas de lunes a viernes y se enviará a 50 clientes cada 20
        minutos. Si quieres cambiar algunos de estos parámetros contacta al equipo de Zeller para que te ayude.
      </Typography>

      <Box display="flex" gap={2}>
        <FormControl fullWidth>
          <InputLabel id="select-template">Seleccionar plantilla</InputLabel>
          <Select
            id="select-template"
            value={selectedTemplate?.name || ''}
            label="Seleccionar plantilla"
            required
            onChange={(e) => {
              const targetTemplate = templates?.find((template) => template.name === e.target.value);
              if (targetTemplate) {
                setSelectedTemplate(targetTemplate);
              }
            }}
          >
            {templates?.map((template) => (
              <MenuItem key={template.id} value={template.name}>
                {template.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" onClick={() => setOpenNewTemplate(true)}>
          Crear nueva plantilla
        </Button>
      </Box>
      {openNewTemplate && (
        <Modal open onClose={() => setOpenNewTemplate(false)}>
          <Paper
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', md: '70%' },
              maxHeight: '90vh',
              overflow: 'auto',
              bgcolor: 'background.paper',
              borderRadius: '16px',
              boxShadow: (theme) => theme.shadows[24],
              p: 4,
            }}
          >
            <CreateTemplateForm businessUnitId={businessUnitId} />
          </Paper>
        </Modal>
      )}

      {selectedTemplate && (
        <>
          <Typography py={1}>
            <b>Define un identificador para la campaña:</b>
          </Typography>
          <TextField
            label="Identificador"
            value={outboundCampaignId || ''}
            onChange={(e) => setOutboundCampaignId(e.target.value)}
            type="text"
            required
            fullWidth
          />
          {currentUser?.isAdmin && newFollowUpConfig && (
            <FollowUpConfigFormAttributes
              followUpConfigDraft={newFollowUpConfig}
              setFollowUpConfigDraft={setNewFollowUpConfig}
              isEditing
              isCampaign
            />
          )}
          {selectedTemplate.components.find((c) => c.type === 'BODY' && c.example?.body_text?.length) && (
            <>
              <Typography py={1}>
                <b>Componentes de la plantilla</b>
                <br />
                Selecciona los atributos que quieres usar en el cuerpo de la plantilla. Estos cambiarán en la vista
                previa.
              </Typography>
              <Autocomplete
                multiple
                id="template-components"
                value={templateBodyParameters || []}
                onChange={(_, value) => handleTemplateBodyParametersChange(value)}
                getOptionLabel={(option) => option.title}
                options={clientAttributes}
                renderInput={(params) => <TextField {...params} variant="standard" placeholder="Atributos" />}
              />
            </>
          )}
          <Box py={1}>
            <Typography variant="body1" pb={1}>
              *Asegúrate de que si la plantilla tiene parámetros, que solamente tenga en el body
            </Typography>
            <PreviewTemplate
              headerEnabled={selectedTemplate.components.some((c) => c.type === 'HEADER')}
              headerText={selectedTemplate.components.find((c) => c.type === 'HEADER')?.text || null}
              headerFormat={selectedTemplate.components.find((c) => c.type === 'HEADER')?.format || null}
              headerFileUrl={
                selectedTemplate.components.find((c) => c.type === 'HEADER' && c.format === 'IMAGE')?.example
                  ?.header_handle?.[0] || null
              }
              bodyText={selectedTemplate.components.find((c) => c.type === 'BODY')?.text || ''}
              bodyVariableExamples={
                (templateBodyParameters?.length && templateBodyParameters.map((p) => p.value)) ||
                selectedTemplate.components.find((c) => c.type === 'BODY')?.example?.body_text?.[0] ||
                null
              }
              footerEnabled={selectedTemplate.components.some((c) => c.type === 'FOOTER')}
              footerText={selectedTemplate.components.find((c) => c.type === 'FOOTER')?.text || null}
              buttons={selectedTemplate.components.find((c) => c.type === 'BUTTONS')?.buttons || null}
            />
          </Box>

          <Box display="flex" gap={2}>
            <PrimaryBtn
              onClick={handleCreateFollowUp}
              startIcon={<EditIcon />}
              disabled={isCreateFollowUpDisabled}
              variant="contained"
            >
              Crear campaña
            </PrimaryBtn>
            {isCreateFollowUpDisabled && (
              <Typography variant="body1" pb={1} color="error">
                *{getDisabledMessage()}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default FollowUpConfigForm;
